import type Luminary from '@lumen5/luminary';

import type { LintFinding } from '../videoLinter';

import { getVideoLanguage } from 'js/src/data/app/app-selectors';
import { getLanguageReadingSpeed } from 'js/src/data/translations';
import { calculateSceneDuration } from 'js/src/libs/timing-utils';
import type { GetRootState } from 'js/src/luminary/luminary-redux-connect-types';
import {
    getEditableSubslideText,
    getSceneGroupCategory,
    isVoiceDrivenSceneGroup,
} from 'js/src/luminary/luminary-selectors';

/**
 * How far below the recommended reading time a scene must fall before we warn.
 * Small overshoots are common and not worth flagging.
 */
const READING_TIME_MARGIN_MS = 500;

/**
 * Warning: a text-on-media scene is meaningfully shorter than the time its
 * on-screen text needs to be read comfortably. Voice-driven scene groups are
 * skipped (their timing is driven by the voiceover, not reading speed).
 * Warning only — the user may have deliberately shortened the scene.
 */
export function checkSceneReadingTime(luminary: Luminary, getState: GetRootState): LintFinding[] {
    const findings: LintFinding[] = [];
    const state = getState();
    const videoLanguage = state.app ? getVideoLanguage(state) : null;
    const readingSpeed = getLanguageReadingSpeed(state, videoLanguage);
    const paceMultiplier = luminary.paceMultiplier;

    luminary.scenes.forEach((scene) => {
        const sceneGroup = scene.sceneGroup;
        if (!sceneGroup || isVoiceDrivenSceneGroup(sceneGroup)) {
            return;
        }

        const sceneTexts = scene.subscenes.map(getEditableSubslideText);
        if (sceneTexts.join('').trim().length === 0) {
            return;
        }

        const sceneCategory = getSceneGroupCategory(sceneGroup);
        const recommendedDuration = calculateSceneDuration(
            sceneTexts,
            sceneCategory,
            paceMultiplier,
            readingSpeed ?? undefined,
        );

        if (scene.duration < recommendedDuration - READING_TIME_MARGIN_MS) {
            const actualSeconds = (scene.duration / 1000).toFixed(1);
            const recommendedSeconds = (recommendedDuration / 1000).toFixed(1);
            findings.push({
                check: 'scene_reading_time',
                severity: 'warning',
                sceneId: scene.id,
                message: `Scene is ${actualSeconds}s but its on-screen text needs about ${recommendedSeconds}s to read comfortably. This may be intentional — confirm the scene isn't too short.`,
            });
        }
    });

    return findings;
}
